/**
 * Builder webfont parity.
 *
 * The A4 builder (email + document) paints its canvas with whatever fonts the
 * USER's OS happens to have — so a preset that asks for "Didot" looks like real
 * macOS Didot in the editor. The PDF renderer (backend openhtmltopdf) only has
 * a fixed set of embedded faces and does NOT have Didot, so it substitutes a
 * different serif. That mismatch is exactly what made the menu preview look
 * fine while the downloaded PDF overlapped.
 *
 * To make editor / HTML-preview / PDF agree, the elegant presets now lead their
 * font stack with 'Playfair Display' — the embedded didone the PDF renderer
 * actually ships (Regular/Bold/Italic). This module loads that same webfont into
 * the browser so the editor canvas and the iframe preview paint Playfair too,
 * instead of falling through to a system serif.
 *
 * The woff2 assets are the ones already self-hosted for the website builder
 * under `${VITE_ASSET_BASE_URL}/shared/fonts/`.
 */

import {
  WB_FONT_FILES,
  wbFontFaceCss,
} from "@/components/builders/website/configurator-panel/components/wbFontPairings";
import { WEBFONT_EXCLUDED_FAMILIES } from "@/lib/fonts/builderFontCatalog";

const ASSET_BASE = import.meta.env.VITE_ASSET_BASE_URL;

/**
 * The builder canvas / gallery / preview paint with the USER's OS fonts, but the
 * PDF renderer (backend openhtmltopdf) embeds a FIXED set under the exact CSS
 * family names the presets emit — Futura→Jost, Georgia→Gelasio, plus Inter,
 * Poppins, IBM Plex Mono, Lora, Playfair. Self-hosting the SAME faces under the
 * SAME family names makes editor/gallery/preview match the downloaded PDF
 * instead of a system fallback (which lies about the design).
 *
 * These family names are only used by preset markup — the app chrome resolves
 * `font-sans`/`font-primary` to `ui-sans-serif` and `system-ui` first — so
 * registering them here does not restyle the builder UI. (The body stack does
 * name Roboto, which the mail editor's extra faces register; it sits behind
 * `system-ui` and is never reached as a primary face.)
 *
 * Each entry: [cssFamily, woff2 basename, weight, style]. Weights are the
 * discrete faces the presets use; the browser interpolates the rest.
 */
const FACES: ReadonlyArray<
  readonly [string, string, number, "normal" | "italic"]
> = [
  ["Futura", "jost-regular", 400, "normal"],
  ["Futura", "jost-bold", 700, "normal"],
  ["Futura", "jost-black", 900, "normal"],
  ["Georgia", "gelasio-regular", 400, "normal"],
  ["Georgia", "gelasio-bold", 700, "normal"],
  ["Inter", "inter-regular", 400, "normal"],
  ["Inter", "inter-bold", 700, "normal"],
  ["Poppins", "poppins-regular", 400, "normal"],
  ["Poppins", "poppins-semibold", 600, "normal"],
  ["Poppins", "poppins-bold", 700, "normal"],
  ["IBM Plex Mono", "ibm-plex-mono-regular", 400, "normal"],
  ["IBM Plex Mono", "ibm-plex-mono-bold", 700, "normal"],
  ["Lora", "lora-regular", 400, "normal"],
  ["Lora", "lora-bold", 700, "normal"],
  ["Lora", "lora-italic", 400, "italic"],
];

/**
 * Every OTHER family the font table hosts, loaded under its own name — for
 * the MAIL editor only.
 *
 * The parity faces above are only seven. The mail inspector offers many more
 * (Roboto, Lato, Open Sans, Nunito, …), and a family nothing loads is painted
 * by the stack's fallback — the picker said "Roboto" and the canvas drew
 * Helvetica. So the mail canvas and its dropdown's per-option preview load
 * every hosted face that is HONEST: the real face under its real name.
 *
 * Left out, on purpose:
 *  - the families FACES / Playfair already register (theirs are the PDF's
 *    files, and a second declaration would only be a second download), and
 *  - `WEBFONT_EXCLUDED_FAMILIES` — the website table's lookalikes (Bodoni Moda
 *    as Didot, Archivo as Neue Haas Grotesk). Registering those here would
 *    replace a Mac's real Didot with a substitute the PDF does not use either.
 *
 * Two surfaces must NOT get them, and that is why they live in a style
 * element of their own:
 *
 *  - The DOCUMENT editor paginates from the live canvas (`offsetHeight`), on
 *    the premise that the canvas draws the faces the PDF embeds. A stored
 *    document whose stack is `'Work Sans', Inter, …` is set in Inter by the
 *    PDF — draw Work Sans on the canvas and its rows measure differently, the
 *    sheets re-split on open, and the editor's page count stops predicting the
 *    PDF's. The document editor therefore REMOVES the element: the builders
 *    share one SPA document, so "never added here" is not enough.
 *  - The sandboxed PREVIEWS render stored / sent html, not the canvas. A mail
 *    shows a webfont there exactly when it declares one itself; loading the
 *    faces from outside would paint Roboto over a stored `font-family: roboto`
 *    that every recipient still gets as Times.
 */
const PARITY_FAMILIES: ReadonlySet<string> = new Set([
  "Playfair Display",
  ...FACES.map(([family]) => family),
]);

export const EDITOR_EXTRA_FAMILIES: readonly string[] = Object.keys(
  WB_FONT_FILES,
).filter((f) => !PARITY_FAMILIES.has(f) && !WEBFONT_EXCLUDED_FAMILIES.has(f));

/** @font-face blocks mirroring the faces the PDF renderer embeds, so the
 *  browser resolves the same family names. */
function buildFontFaceCss(): string {
  const face = (family: string, file: string, weight: number | string, style: string) => `
@font-face {
  font-family: '${family}';
  src: url("${ASSET_BASE}/shared/fonts/${file}.woff2") format("woff2");
  font-weight: ${weight};
  font-style: ${style};
  font-display: swap;
}`;

  // Playfair ships upright-only; the browser synthesizes italic for the `<i>`
  // runs the presets use — matches the PDF's real italic closely enough.
  const playfair =
    face("Playfair Display", "playfair-display", "400 700", "normal") +
    face("Playfair Display", "playfair-display", "400 700", "italic");

  const rest = FACES.map(([fam, file, weight, style]) =>
    face(fam, file, weight, style),
  ).join("");

  return (playfair + rest).trim();
}

const STYLE_ID = "veltric-builder-fonts";
const EXTRA_STYLE_ID = "veltric-builder-fonts-extra";

/**
 * Idempotently inject the builder webfonts into a document's <head>.
 * Defaults to the top-level document (editor canvas); pass an iframe's
 * contentDocument to load the parity faces into a sandboxed preview.
 *
 * `extras` decides the mail-only faces (see `EDITOR_EXTRA_FAMILIES`):
 *  - `true`  — add them (the mail editor, which removes them on unmount),
 *  - `false` — REMOVE them if an earlier screen added them (document editor),
 *  - unset   — leave them alone (previews: a fresh frame document has none).
 */
export function ensureBuilderFonts(
  doc: Document = document,
  opts: { extras?: boolean } = {},
): void {
  // VITE_ASSET_BASE_URL is required for the CDN-hosted woff2; without it the
  // url() would be malformed, so skip injection and let the system fallback
  // stand rather than emit a broken @font-face.
  if (!ASSET_BASE) return;
  if (!doc) return;
  const head = doc.head ?? doc.documentElement;

  if (!doc.getElementById(STYLE_ID)) {
    const style = doc.createElement("style");
    style.id = STYLE_ID;
    style.textContent = buildFontFaceCss();
    head.appendChild(style);
  }

  if (opts.extras === true) {
    if (doc.getElementById(EXTRA_STYLE_ID)) return;
    const css = wbFontFaceCss(EDITOR_EXTRA_FAMILIES, ASSET_BASE);
    if (!css) return;
    const extra = doc.createElement("style");
    extra.id = EXTRA_STYLE_ID;
    extra.textContent = css;
    head.appendChild(extra);
  } else if (opts.extras === false) {
    doc.getElementById(EXTRA_STYLE_ID)?.remove();
  }
}
